<template>
  <AppLayout :title="$t('Neuigkeiten')">
    <div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white shadow">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div class="md:flex md:items-center md:justify-between">
            <div class="flex-1 min-w-0">
              <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {{ $t('Neuigkeiten') }}
              </h2>
            </div>
          </div>
        </div>
      </div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- News Items -->
        <div class="space-y-6">
          <div
            v-for="item in news"
            :key="item.id"
            class="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div class="p-6">
              <div class="flex items-start space-x-4">
                <div v-if="item.featured_image_path" class="flex-shrink-0">
                  <img
                    :src="getImageUrl(item.featured_image_path)"
                    :alt="item.title"
                    class="w-20 h-20 rounded-lg object-cover"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between">
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">
                      {{ item.title }}
                    </h3>
                    <span class="text-sm text-gray-500">
                      {{ formatDate(item.date) }}
                    </span>
                  </div>
                  <p v-if="item.subtitle" class="text-gray-600 mb-3">
                    {{ item.subtitle }}
                  </p>
                  <div class="prose max-w-none text-gray-700" v-html="truncateContent(item.content)"></div>
                  <div class="mt-4">
                    <a
                      :href="`/news/${item.id}`"
                      class="text-sonnenglas-600 hover:text-sonnenglas-700 font-medium"
                    >
                      {{ $t('Weiterlesen') }} →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination && pagination.last_page > 1" class="mt-8">
          <nav class="flex items-center justify-between">
            <div class="flex-1 flex justify-between sm:hidden">
              <Link
                v-if="pagination.current_page > 1"
                :href="getPaginationUrl(pagination.current_page - 1)"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {{ $t('Vorherige') }}
              </Link>
              <Link
                v-if="pagination.current_page < pagination.last_page"
                :href="getPaginationUrl(pagination.current_page + 1)"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {{ $t('Nächste') }}
              </Link>
            </div>
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p class="text-sm text-gray-700">
                  {{ $t('Zeige') }}
                  <span class="font-medium">{{ pagination.from }}</span>
                  {{ $t('bis') }}
                  <span class="font-medium">{{ pagination.to }}</span>
                  {{ $t('von') }}
                  <span class="font-medium">{{ pagination.total }}</span>
                  {{ $t('results') }}
                </p>
              </div>
              <div>
                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <Link
                    v-if="pagination.current_page > 1"
                    :href="getPaginationUrl(pagination.current_page - 1)"
                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    ‹
                  </Link>
                  <span
                    v-for="page in getPageNumbers()"
                    :key="page"
                    class="relative inline-flex items-center px-4 py-2 border text-sm font-medium"
                    :class="page === pagination.current_page
                      ? 'z-10 bg-sonnenglas-50 border-sonnenglas-500 text-sonnenglas-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'"
                  >
                    <Link
                      v-if="page !== pagination.current_page"
                      :href="getPaginationUrl(page)"
                    >
                      {{ page }}
                    </Link>
                    <span v-else>{{ page }}</span>
                  </span>
                  <Link
                    v-if="pagination.current_page < pagination.last_page"
                    :href="getPaginationUrl(pagination.current_page + 1)"
                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    ›
                  </Link>
                </nav>
              </div>
            </div>
          </nav>
        </div>

        <!-- Empty State -->
        <div v-if="news.length === 0" class="text-center py-12">
          <div class="text-gray-500">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3v6m0 0l4-2m-4 2L9 9" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('Keine Neuigkeiten gefunden') }}</h3>
            <p class="mt-1 text-sm text-gray-500">{{ $t('Es gibt keine Neuigkeiten anzuzeigen.') }}</p>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup>
import { inject } from 'vue'
import { Link } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const route = inject('route')

const props = defineProps({
  news: Array,
  pagination: Object,
})

const formatDate = (dateString) => {
  const date = new Date(dateString)
  const locale = document.documentElement.lang || 'de-DE'
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const truncateContent = (content) => {
  if (!content) return ''
  const text = content.replace(/<[^>]*>/g, '')
  return text.length > 200 ? text.substring(0, 200) + '...' : text
}

const getImageUrl = (path) => {
  // This would fetch the image from the API - for now return a placeholder
  return `/api/partner-portal/images/${path}`
}

const getPaginationUrl = (page) => {
  return route('news', { page })
}

const getPageNumbers = () => {
  if (!props.pagination) return []

  const current = props.pagination.current_page
  const last = props.pagination.last_page
  const pages = []

  // Show up to 5 pages around current page
  const start = Math.max(1, current - 2)
  const end = Math.min(last, current + 2)

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
}
</script>